import React from "react";
import { motion } from "framer-motion";
import { Star } from "lucide-react";
import { TESTIMONIALS } from "../data/content";
import { Reveal, Eyebrow, EASE } from "./Reveal";

const Stars = ({ n = 5, className = "" }) => (
  <div className={`flex gap-1 ${className}`} aria-label={`${n} star rating`}>
    {Array.from({ length: n }).map((_, i) => (
      <Star key={i} size={15} className="fill-[#CBA135] text-[#CBA135]" />
    ))}
  </div>
);

export const Testimonials = () => {
  const [featured, ...rest] = TESTIMONIALS;

  return (
    <section
      id="reviews"
      className="relative bg-[#F4EBD9] py-24 sm:py-36 px-6 sm:px-10 md:px-16"
      data-testid="testimonials"
    >
      <div className="max-w-6xl mx-auto grid lg:grid-cols-12 gap-10 lg:gap-14 items-start">
        {/* Left: intro + large featured review */}
        <div className="lg:col-span-6 lg:sticky lg:top-28">
          <Reveal>
            <Eyebrow className="text-[#9C7526]">Reviews</Eyebrow>
          </Reveal>
          <Reveal delay={0.1}>
            <h2 className="mt-5 font-serif font-medium text-[#21070D] text-4xl sm:text-5xl lg:text-6xl leading-[1.02] tracking-tight">
              Stories of clarity &amp; healing
            </h2>
          </Reveal>

          <Reveal delay={0.16}>
            <figure className="mt-10 relative rounded-[2rem] bg-[#21070D] text-[#F4EBD9] p-9 sm:p-11 overflow-hidden">
              <span className="font-serif text-[7rem] leading-[0.6] text-[#CBA135]/30 select-none absolute top-6 left-7">
                “
              </span>
              <Stars className="relative" />
              <blockquote className="relative mt-5 font-serif text-2xl sm:text-3xl leading-snug italic text-[#F9F1E2]">
                {featured.quote}
              </blockquote>
              <figcaption className="relative mt-7 flex items-center gap-4">
                <span className="grid place-items-center h-12 w-12 rounded-full bg-[#CBA135] text-[#21070D] font-serif text-xl">
                  {featured.name.charAt(0)}
                </span>
                <span>
                  <span className="block font-medium text-[#F9F1E2]">
                    {featured.name}
                  </span>
                  <span className="text-[#EBD08A] text-sm">{featured.role}</span>
                </span>
              </figcaption>
            </figure>
          </Reveal>
        </div>

        {/* Right: staggered smaller reviews */}
        <div className="lg:col-span-6 space-y-6">
          {rest.map((t, i) => (
            <motion.figure
              key={i}
              data-testid={`testimonial-${i}`}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.8, ease: EASE, delay: i * 0.08 }}
              className={`rounded-2xl bg-[#FBF3E3] border border-[#CBA135]/25 p-7 shadow-[0_14px_40px_-26px_rgba(33,7,13,0.5)] ${
                i % 2 === 1 ? "lg:ml-10" : "lg:mr-10"
              }`}
            >
              <div className="flex items-center justify-between">
                <Stars />
                <span className="font-serif text-3xl text-[#CBA135]/40 leading-none">
                  ”
                </span>
              </div>
              <blockquote className="mt-4 text-[#3A1219] text-lg leading-relaxed body-serif">
                {t.quote}
              </blockquote>
              <figcaption className="mt-5 flex items-center gap-3">
                <span className="grid place-items-center h-9 w-9 rounded-full bg-[#CBA135]/15 text-[#9C7526] font-serif">
                  {t.name.charAt(0)}
                </span>
                <span className="text-sm">
                  <span className="font-medium text-[#21070D]">{t.name}</span>
                  <span className="text-[#9C7526]"> · {t.role}</span>
                </span>
              </figcaption>
            </motion.figure>
          ))}
        </div>
      </div>
    </section>
  );
};
